import { useEffect } from 'react'
import { Switch, Text } from '@mantine/core'
import type { TaskArgsComponentProps } from '../args-registry'
import { DEFAULT_UNCONDITIONAL, extractUnconditional } from './UspstfDownloadArgs.utils'

/**
 * Args editor/viewer for cpg.uspstf.download: { unconditional }. false = conditional
 * download (a 304 from the source skips the fetch entirely); true = always download the
 * full corpus — it is still stored only when the content hash changed.
 */
export function UspstfDownloadArgs({ value, onChange, readOnly }: TaskArgsComponentProps) {
  const unconditional = extractUnconditional(value)

  // Edit mode seeded with no args (scheduled_args unset): fill in the runner's default so
  // the dialog opens valid and the posted run carries explicit args.
  useEffect(() => {
    if (!readOnly && unconditional === undefined) {
      onChange?.({ unconditional: DEFAULT_UNCONDITIONAL })
    }
  }, [readOnly, unconditional, onChange])

  if (readOnly) {
    // A run without args (e.g. scheduled before scheduled_args was seeded) used the default.
    return (
      <Text size="sm">
        {(typeof unconditional === 'boolean' ? unconditional : DEFAULT_UNCONDITIONAL) ? (
          <>
            <b>Unconditional</b> download — full corpus fetched, stored only if changed
          </>
        ) : (
          <>
            <b>Conditional</b> download — skipped when the source reports not modified
            {typeof unconditional === 'boolean' ? '' : ' (default)'}
          </>
        )}
      </Text>
    )
  }

  return (
    <Switch
      label="Unconditional download"
      description="Always download the full corpus instead of the conditional check (ETag / Last-Modified); it is still stored only when the content changed. Use to force a re-check."
      checked={unconditional === true}
      onChange={(event) => onChange?.({ unconditional: event.currentTarget.checked })}
    />
  )
}
